if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.initProductMediaViewToggle();
      }

      disconnectedCallback() {
        this.productMediaViewVariantUnsubscriber?.();
        if (this.productMediaViewLayoutHandler) {
          window.removeEventListener('resize', this.productMediaViewLayoutHandler);
          window.removeEventListener('scroll', this.productMediaViewLayoutHandler, true);
        }
      }

      getMediaElement(mediaId) {
        if (!mediaId) return null;

        let cleanMediaId = String(mediaId);
        if (cleanMediaId.indexOf('-') !== -1) {
          const parts = cleanMediaId.split('-');
          cleanMediaId = parts[parts.length - 1];
        }

        const candidates = Array.from(
          this.querySelectorAll(`[data-media-id="${cleanMediaId}"], [data-media-id="${mediaId}"]`)
        );

        return candidates.find((element) => this.isElementVisible(element)) || candidates[0] || null;
      }

      initProductMediaViewToggle() {
        if (this.productMediaViewInitialized) return;

        this.productMediaViewDataScript = this.querySelector('[data-product-media-view-data]');
        this.productMediaViewToggle = this.querySelector('[data-product-media-view-toggle]');
        this.productMediaViewOpenButton = this.querySelector('[data-product-media-view-button="open"]');
        this.productMediaViewPrivacyButton = this.querySelector('[data-product-media-view-button="privacy"]');
        this.productMediaViewPrivacyOverlay = this.querySelector('[data-product-media-privacy-overlay]');
        this.productMediaViewPrivacyImage = this.querySelector('[data-product-media-privacy-image]');

        if (
          !this.productMediaViewDataScript ||
          !this.productMediaViewToggle ||
          !this.productMediaViewOpenButton ||
          !this.productMediaViewPrivacyButton ||
          !this.productMediaViewPrivacyOverlay ||
          !this.productMediaViewPrivacyImage
        ) {
          return;
        }

        try {
          this.productMediaViewData = JSON.parse(this.productMediaViewDataScript.textContent || '{}');
        } catch (e) {
          this.productMediaViewData = null;
        }

        if (!this.productMediaViewData?.variants) return;

        this.productMediaViewInitialized = true;
        this.productMediaViewMode = 'open';
        this.productMediaViewVariantId = this.getCurrentVariantId() || this.productMediaViewData.initialVariantId;
        this.productMediaViewHasAnyPrivacy = Object.values(this.productMediaViewData.variants).some(
          (variantData) => !!variantData?.privacyImage
        );

        this.productMediaViewOpenButton.addEventListener('click', () => this.showOpenVariantView());
        this.productMediaViewPrivacyButton.addEventListener('click', () => this.showPrivacyVariantView());

        this.productMediaViewLayoutHandler = () => {
          if (this.productMediaViewMode === 'privacy') this.placePrivacyOverlay();
        };
        window.addEventListener('resize', this.productMediaViewLayoutHandler);
        window.addEventListener('scroll', this.productMediaViewLayoutHandler, true);

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.productMediaViewVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.dataset.section) return;
            this.updateVariantViewData(data?.variant?.id);
          });
        }

        this.updateVariantViewData(this.productMediaViewVariantId, { keepPrivacy: false });
      }

      getCurrentVariantId() {
        const selectedVariantScript = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariantScript) return null;

        try {
          return JSON.parse(selectedVariantScript.textContent || '{}')?.id || null;
        } catch (e) {
          return null;
        }
      }

      getCurrentVariantViewData() {
        if (!this.productMediaViewVariantId) return null;
        return this.productMediaViewData?.variants?.[String(this.productMediaViewVariantId)] || null;
      }

      updateVariantViewData(variantId, options = {}) {
        if (!this.productMediaViewInitialized) return;
        if (variantId) this.productMediaViewVariantId = String(variantId);

        const variantData = this.getCurrentVariantViewData();
        const hasPrivacyImage = !!variantData?.privacyImage;

        this.productMediaViewToggle.hidden = !this.productMediaViewHasAnyPrivacy;
        this.productMediaViewPrivacyButton.disabled = !hasPrivacyImage;
        this.productMediaViewPrivacyButton.setAttribute('aria-disabled', String(!hasPrivacyImage));

        if (!hasPrivacyImage && this.productMediaViewMode === 'privacy') {
          this.showOpenVariantView({ transitionMedia: false });
          return;
        }

        if (this.productMediaViewMode === 'privacy' && options.keepPrivacy !== false) {
          this.showPrivacyVariantView({ transitionMedia: false });
        } else {
          this.updateProductMediaViewButtons();
        }
      }

      updateProductMediaViewButtons() {
        if (!this.productMediaViewOpenButton || !this.productMediaViewPrivacyButton) return;

        this.productMediaViewOpenButton.setAttribute('aria-pressed', String(this.productMediaViewMode === 'open'));
        this.productMediaViewPrivacyButton.setAttribute('aria-pressed', String(this.productMediaViewMode === 'privacy'));
      }

      async showOpenVariantView(options = {}) {
        if (!this.productMediaViewInitialized) return;

        this.productMediaViewMode = 'open';
        this.updateProductMediaViewButtons();
        this.hidePrivacyOverlay();
        this.fadeOutVariantOverlay(this.productMediaViewHeldOverlay);
        this.productMediaViewHeldOverlay = null;

        const variantData = this.getCurrentVariantViewData();
        const shouldTransitionMedia = options.transitionMedia !== false;
        if (shouldTransitionMedia && variantData?.openMediaId) {
          await this.transitionToMedia(`${this.dataset.section}-${variantData.openMediaId}`);
        }
      }

      async showPrivacyVariantView(options = {}) {
        if (!this.productMediaViewInitialized) return;

        const variantData = this.getCurrentVariantViewData();
        if (!variantData?.privacyImage) return;

        this.productMediaViewMode = 'privacy';
        this.updateProductMediaViewButtons();

        if (options.transitionMedia !== false && variantData.openMediaId) {
          await this.transitionToMedia(`${this.dataset.section}-${variantData.openMediaId}`);
        }

        await this.preloadPrivacyImage(variantData.privacyImage);
        if (variantData.privacyImageWidth && variantData.privacyImageHeight) {
          this.productMediaViewPrivacyImage.width = variantData.privacyImageWidth;
          this.productMediaViewPrivacyImage.height = variantData.privacyImageHeight;
        }
        this.productMediaViewPrivacyImage.src = variantData.privacyImage;
        this.placePrivacyOverlay();
        this.productMediaViewPrivacyOverlay.hidden = false;

        requestAnimationFrame(() => {
          this.productMediaViewPrivacyOverlay.classList.add('is-visible');
          this.fadeOutVariantOverlay(this.productMediaViewHeldOverlay);
          this.productMediaViewHeldOverlay = null;
        });
      }

      hidePrivacyOverlay() {
        if (!this.productMediaViewPrivacyOverlay) return;

        this.productMediaViewPrivacyOverlay.classList.remove('is-visible');
        setTimeout(() => {
          if (this.productMediaViewMode !== 'privacy') {
            this.productMediaViewPrivacyOverlay.hidden = true;
          }
        }, 280);
      }

      placePrivacyOverlay() {
        if (!this.productMediaViewPrivacyOverlay) return false;

        const activeMedia = this.getActiveMediaElement();
        const overlayHost = activeMedia?.closest('.product-media-container, .media-gallery__grid-item, .swiper-slide');
        if (!overlayHost) return false;

        if (window.getComputedStyle(overlayHost).position === 'static') {
          overlayHost.style.position = 'relative';
        }

        if (this.productMediaViewPrivacyOverlay.parentElement !== overlayHost) {
          overlayHost.appendChild(this.productMediaViewPrivacyOverlay);
        }

        return true;
      }

      preloadPrivacyImage(imageUrl) {
        if (!imageUrl) return Promise.resolve();

        return new Promise((resolve) => {
          const image = new Image();
          let complete = false;
          const finish = () => {
            if (complete) return;
            complete = true;
            resolve();
          };

          image.decoding = 'async';
          image.addEventListener('load', () => {
            if (typeof image.decode === 'function') {
              image.decode().then(finish).catch(finish);
            } else {
              finish();
            }
          }, { once: true });
          image.addEventListener('error', finish, { once: true });
          image.src = imageUrl;

          setTimeout(finish, 6000);
        });
      }

      getActiveMediaElement() {
        const candidates = Array.from(
          this.querySelectorAll(
            '[data-media-id].active, .swiper-slide-active [data-media-id], .media-gallery__grid [data-media-id], [data-media-id]'
          )
        );

        return candidates.find((element) => this.isElementVisible(element)) || candidates[0] || null;
      }

      isElementVisible(element) {
        if (!element) return false;

        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height) return false;

        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      }

      async transitionToMedia(mediaId) {
        const targetMedia = this.getMediaElement(mediaId);
        if (!targetMedia) return false;

        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const overlay = reduceMotion ? null : this.createVariantFadeOverlay();
        const shouldHoldPrivacyOverlay = this.productMediaViewMode === 'privacy' && overlay;

        await this.waitForImages(targetMedia);

        const didActivate = this.setActiveMedia(mediaId, false);
        if (shouldHoldPrivacyOverlay) {
          this.productMediaViewHeldOverlay = overlay;
        } else if (overlay) {
          this.fadeOutVariantOverlay(overlay);
        }

        return didActivate;
      }

      createVariantFadeOverlay() {
        const activeMedia = this.getActiveMediaElement();
        const visiblePrivacyOverlay =
          this.productMediaViewPrivacyOverlay &&
          !this.productMediaViewPrivacyOverlay.hidden &&
          this.productMediaViewPrivacyOverlay.classList.contains('is-visible');
        const privacyImage = visiblePrivacyOverlay ? this.productMediaViewPrivacyImage : null;
        const image = privacyImage || activeMedia?.querySelector('img');
        const imageSource = image?.currentSrc || image?.src;

        if (!activeMedia || !image || !imageSource) return null;

        const rect = (visiblePrivacyOverlay ? this.productMediaViewPrivacyOverlay : activeMedia).getBoundingClientRect();
        if (!rect.width || !rect.height) return null;

        const imageStyle = window.getComputedStyle(image);
        const overlay = document.createElement('div');

        overlay.className = 'media-gallery__variant-fade-overlay';
        overlay.style.position = 'fixed';
        overlay.style.left = `${rect.left}px`;
        overlay.style.top = `${rect.top}px`;
        overlay.style.width = `${rect.width}px`;
        overlay.style.height = `${rect.height}px`;
        overlay.style.backgroundImage = `url("${imageSource.replace(/"/g, '\\"')}")`;
        overlay.style.backgroundSize = imageStyle.objectFit || 'contain';
        overlay.style.backgroundPosition = imageStyle.objectPosition || 'center center';
        overlay.style.backgroundRepeat = 'no-repeat';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '2147483000';
        overlay.style.opacity = '1';
        overlay.style.transition = 'opacity 420ms ease';
        overlay.style.willChange = 'opacity';

        document.body.appendChild(overlay);

        return overlay;
      }

      fadeOutVariantOverlay(overlay) {
        if (!overlay) return;

        requestAnimationFrame(() => {
          overlay.style.opacity = '0';
          setTimeout(() => overlay.remove(), 460);
        });
      }

      setActiveMedia(mediaId, smooth = false) {
        if (!mediaId) return false;

        const element = this.getMediaElement(mediaId);
        if (!element) return false;

        this.querySelectorAll('[data-media-id].active').forEach((node) => node.classList.remove('active'));

        element.classList.add('active');

        const allMediaElements = Array.from(this.querySelectorAll('[data-media-id]'));
        const mediaIndex = allMediaElements.indexOf(element);

        this.syncSlideshow(element, mediaIndex, smooth);
        this.syncThumbnails(element, mediaIndex);

        if (this.classList.contains('media-gallery--grid')) {
          this.swapGridMedia(element, mediaIndex);
        }

        return true;
      }

      /**
       * Sync slideshow/swiper component when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       * @param {boolean} smooth - Whether to use smooth transition
       */
      syncSlideshow(mediaElement, mediaIndex, smooth = false) {
        try {
          if (!this.classList.contains('media-gallery--carousel')) return;

          const swiperComponent = this.querySelector('swiper-component');
          if (!swiperComponent) return;
          
          const waitForSwiper = (retries = 10) => {
            if (swiperComponent.initSwiper) {
              const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
              let slideIndex = mediaIndex;
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              }

              if (slideIndex >= 0 && slideIndex < slides.length && swiperComponent.initSwiper) {
                swiperComponent.initSwiper.slideTo(slideIndex, smooth ? 800 : 0);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForSwiper(retries - 1), 50);
            }
          };

          waitForSwiper();
        } catch (e) {
          try {
            if (this.slideshow && typeof this.slideshow.goTo === 'function') {
              this.slideshow.goTo(mediaIndex);
            }
          } catch (err) {
            // silent
          }
        }
      }

      /**
       * Sync thumbnails controls when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      syncThumbnails(mediaElement, mediaIndex) {
        try {
          const thumbnailsContainer = this.querySelector('.swiper-controls__thumbnails-container');
          if (!thumbnailsContainer) return;

          const thumbnailButtons = Array.from(thumbnailsContainer.querySelectorAll('.swiper-controls__thumbnail[data-index]'));
          if (thumbnailButtons.length === 0) return;

          let slideIndex = mediaIndex;
          
          if (this.classList.contains('media-gallery--carousel')) {
            const swiperComponent = this.querySelector('swiper-component');
            if (swiperComponent) {
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              } else {
                if (swiperComponent.initSwiper && typeof swiperComponent.initSwiper.activeIndex === 'number') {
                  slideIndex = mediaIndex;
                }
              }
            }
          }

          let targetThumbnail = thumbnailButtons.find((button) => {
            const index = parseInt(button.getAttribute('data-index'), 10);
            return !isNaN(index) && index === slideIndex;
          });

          if (!targetThumbnail) {
            targetThumbnail = thumbnailButtons.find((button) => {
              const index = parseInt(button.getAttribute('data-index'), 10);
              return !isNaN(index) && index === mediaIndex;
            });
          }

          if (targetThumbnail) {
            const finalSlideIndex = parseInt(targetThumbnail.getAttribute('data-index'), 10);

            this.syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, finalSlideIndex);
          }
        } catch (e) {
          // silent
        }
      }

      /**
       * Sync thumbnail swiper to show the active thumbnail
       * @param {HTMLElement} thumbnailsContainer - The thumbnail container element
       * @param {Array} thumbnailButtons - Array of all thumbnail buttons
       * @param {HTMLElement} targetThumbnail - The active thumbnail button
       * @param {number} slideIndex - The slide index to sync to
       */
      syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, slideIndex) {
        try {
          const waitForThumbnailSwiper = (retries = 10) => {
            const swiperComponent = this.querySelector('swiper-component');
            let thumbsSwiper = null;

            if (swiperComponent && swiperComponent.initSwiper && swiperComponent.initSwiper.params && swiperComponent.initSwiper.params.thumbs) {
              thumbsSwiper = swiperComponent.initSwiper.params.thumbs.swiper;
            }

            if (!thumbsSwiper) {
              const thumbnailSwiper = thumbnailsContainer.querySelector('.swiper');
              if (thumbnailSwiper && thumbnailSwiper.swiper) {
                thumbsSwiper = thumbnailSwiper.swiper;
              }
            }

            if (thumbsSwiper) {
              if (slideIndex >= 0 && slideIndex < thumbnailButtons.length) {
                thumbsSwiper.slideTo(slideIndex, 300);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForThumbnailSwiper(retries - 1), 50);
            }
          };

          waitForThumbnailSwiper();
        } catch (e) {
          // silent
        }
      }

      /**
       * Swap media in grid layout: move variant image to first position
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      swapGridMedia() {
        try {
          if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
            const handleVariantUpdateOnce = async (event) => {
              let overlay = null;
              try {
                const source = event?.detail?.data?.html;
                if (!source) return;

                const newMediaGallery = source.querySelector('media-gallery');
                if (!newMediaGallery) return;

                overlay = this.createVariantFadeOverlay();
                await this.waitForImages(newMediaGallery);
                this.replaceWith(newMediaGallery);
                this.fadeOutVariantOverlay(overlay);

                try {
                  newMediaGallery.scrollIntoView({ behavior: 'smooth' });
                } catch (e) {
                  // ignore scrolling errors
                }
              } catch (e) {
                this.fadeOutVariantOverlay(overlay);
                // silent
              } finally {
                document.removeEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
              }
            };
            document.addEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
          }
          
        } catch (e) {
          // silent
        }
      }

      waitForImages(root) {
        if (!root) return Promise.resolve();

        const imageScope =
          root.querySelector('.active') ||
          root.querySelector('[data-media-id]') ||
          root;
        const images = Array.from(imageScope.querySelectorAll('img'));
        const pendingImages = images.filter((image) => !image.complete);

        if (!pendingImages.length) return Promise.resolve();

        pendingImages.forEach((image) => {
          image.loading = 'eager';
        });

        return Promise.race([
          Promise.all(
            pendingImages.map(
              (image) => this.preloadImage(image)
            )
          ),
          new Promise((resolve) => setTimeout(resolve, 6000)),
        ]);
      }

      preloadImage(image) {
        return new Promise((resolve) => {
          let complete = false;
          const finish = () => {
            if (complete) return;
            complete = true;
            resolve();
          };
          const finishAfterDecode = (target) => {
            if (target && typeof target.decode === 'function') {
              target.decode().then(finish).catch(finish);
            } else {
              finish();
            }
          };

          image.addEventListener('load', () => finishAfterDecode(image), { once: true });
          image.addEventListener('error', finish, { once: true });

          if (image.complete) {
            finishAfterDecode(image);
            return;
          }

          const preloader = new Image();
          preloader.decoding = 'async';
          if (image.sizes) preloader.sizes = image.sizes;
          if (image.srcset) preloader.srcset = image.srcset;
          preloader.addEventListener('load', () => finishAfterDecode(preloader), { once: true });
          preloader.addEventListener('error', finish, { once: true });
          const preloadSource = image.currentSrc || image.src;
          if (!preloadSource) {
            finish();
            return;
          }
          preloader.src = preloadSource;
        });
      }
    }
  );
}
